<template>
  <section class="task-tab">
    <QueryErrorNotice :error="taskTabErrors" />

    <EmptyStateNotice
      v-if="!items.length && !isPending"
      :text="t('emptyTasks')"
      variant="info"
    />

    <div v-else class="task-list">
      <article
        v-for="(item, index) in items"
        :key="`task-${item.id}-${index}`"
        class="task-item"
        :class="getBg(item).bg"
      >
        <button
          class="acc-item without-outline"
          type="button"
          @click="toggleTask(item.id)"
        >
          <div>
            <p class="f-14 mb-0 bold collapse-title">
              {{ t("theTasks") }} №{{ index + 1 }}
            </p>
            <div class="f-12 d-flex flex-column">
              <span v-if="taskEndDate(item).status" class="status red--color">
                {{ taskEndDate(item).text }} {{ taskEndDate(item).date }}
              </span>
              <span v-else class="status" :class="getStatus(item).class">
                {{ getStatus(item).status }}
              </span>
              <span class="statusTime" v-if="item.answer.updated_ts && [1, 4].includes(getStatus(item).num)">
                {{ formatSendDate(item.answer.updated_ts) }}
              </span>
            </div>
          </div>
          <div class="acc-icon" :class="{ 'is-open': isTaskOpen(item.id) }">
            <span class="material-icons">keyboard_arrow_down</span>
          </div>
        </button>

        <div
          v-if="[1, 2, 3, 4].includes(getStatus(item).num) && getComment(item)"
          class="message-alert p-3"
          :class="getBg(item).bg"
        >
          <p class="bold mb-2 f-14" :class="getBg(item).text">{{ t("commentTask") }}</p>
          <p class="mb-0 f-13" :class="getBg(item).text">{{ getComment(item) }}</p>
        </div>

        <div v-if="isTaskOpen(item.id)">
          <div class="task__header py-3 px-3">
            <div class="task__header__title">
              <h1 class="task__title bold f-16">{{ item.name }}</h1>
              <h3 v-if="Number(item.time) !== -1" class="task__sub-title bold f-12">
                {{ t("timeToReady") }}: {{ item.time }}
              </h3>
            </div>

            <div
              v-if="item.content"
              class="task__content f-13 my-3"
              v-html="item.content"
              @click="onTaskContentClick"
            />

            <div v-if="taskFiles(item).length" class="mb-3">
              <p class="f-13 bold mb-1">{{ t("uploadFile") }}</p>
              <FileFilter
                v-for="(file, fileIndex) in taskFiles(item)"
                :key="`task-file-${item.id}-${fileIndex}`"
                :url="file.url"
                :name="file.name"
                :file="file"
                @open-file-viewer="openFrame"
              />
            </div>
          </div>

          <div v-if="parentRoleCheck(item)" class="task__footer py-3 pl-4 pr-3">
            <div class="answer__head">
              <span class="f-14 bold mb-2">{{ t("answer") }}</span>
              <button
                v-if="[1, 4].includes(getStatus(item).num) && roleId === 3"
                class="btn__revert"
                type="button"
                @click="revertTask(item)"
              >
                {{ t("revert") }}
              </button>
            </div>

            <textarea
              v-model="item.answer.text"
              rows="5"
              class="textarea mb-2 without-outline"
              :disabled="isAnswerDisabled(item)"
            />
            <p v-if="item.answer.text.length >= 2000">
              <small class="text-danger">{{ t("maxAnswerError") }}</small>
            </p>

            <div
              v-if="canEditAnswer(item) || item.answer.chat.length"
              class="task__answer-files mb-3"
            >
              <span class="f-13 bold">{{ t("uploadedFileInAnswer") }}</span>
              <div
                class="child_file--btn mt-2"
                @click="checkMoreThen(item.answer.files.length)"
              >
                <input
                  :id="fileInputId(item.id)"
                  :ref="(el) => setFileInputRef(item.id, el)"
                  type="file"
                  multiple
                  :disabled="isFileUploadDisabled(item)"
                  accept=".txt,.htm,.html,.rtf,.doc,.docx,.xls,.xlsx,.pdf,.mp3,.waf,.mp4,.avi,.ppt,.pptx,.flipchart,.cptx,.zip,.gz,.rar,.flr,.flp,.svg,.jpg,.png,.gif,.jpeg,.tif,.tiff,.bmp"
                  @change="uploadFiles($event, item)"
                />
                <label :for="fileInputId(item.id)">
                  {{ t("uploadToAnswer") }}
                  <span class="material-icons ml-1">cloud_upload</span>
                </label>
              </div>
            </div>

            <div
              v-for="(file, fileIndex) in item.answer.files"
              :key="`answer-file-${item.id}-${fileIndex}`"
              class="d-flex justify-content-between align-items-center mb-3"
            >
              <template v-if="!file.is_deleted">
                <span class="taskUploadFileName">
                  {{ file.name }}
                </span>
                <div class="link-item--green d-flex align-items-center">
                  <span class="material-icons mx-3 green-icon" @click="openFile(file)">
                    cloud_download
                  </span>
                  <span
                    v-if="getByFileType(file)"
                    class="material-icons link mr-3 green-icon"
                    @click="openFrame(file)"
                  >
                    launch
                  </span>
                  <span
                    v-if="canEditAnswer(item) && roleId === 3"
                    class="material-icons mr-1"
                    @click="removeFile(item, fileIndex)"
                  >
                    close
                  </span>
                </div>
              </template>
            </div>

            <div v-if="inProgress" class="progress-wrap mb-3">
              <div class="progress">
                <div class="progress-bar" :style="{ width: `${progress}%` }">
                  {{ progress }}%
                </div>
              </div>
            </div>

            <div
              v-if="canEditAnswer(item) || item.answer.chat.length"
              class="mb-3 chat__body"
            >
              <div class="chat__msg">
                <div class="mb-2 bold">{{ t("commentTask") }}:</div>
                <div
                  v-for="(msg, msgIndex) in item.answer.chat"
                  :key="`msg-${msgIndex}`"
                  class="mb-1 ml-1 f-14"
                >
                  <span>{{ msg.person_name }}:</span>
                  <span>{{ msg.message }}</span>
                </div>
              </div>
              <input
                v-model="item.answer.childMessage"
                class="input mb-2 without-outline"
                :disabled="isAnswerDisabled(item)"
              />
            </div>

            <button
              v-if="roleId === 3"
              class="submit f-13 btn-custom"
              :disabled="isSubmitDisabled(item)"
              @click="submit(item)"
            >
              {{ t("sendToCheck") }}
            </button>
          </div>

          <div v-else class="parentAlert">{{ t("parentAlert") }}</div>
        </div>
      </article>
    </div>

    <FileViewer v-model="iframe" />
  </section>
</template>

<script setup>
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import EmptyStateNotice from "@/components/common/EmptyStateNotice.vue";
import FileFilter from "@/components/common/FileFilter.vue";
import FileViewer from "@/components/common/FileViewer.vue";
import QueryErrorNotice from "@/components/common/QueryErrorNotice.vue";
import {
  useCreateTaskAnswerMutation,
  useTaskTasksQuery,
  useUpdateTaskAnswerMutation,
  useUploadTaskFileMutation,
} from "@/queries";

const props = defineProps({
  lessonId: {
    type: Number,
    default: 0,
  },
  pupilId: {
    type: Number,
    default: 0,
  },
  roleId: {
    type: Number,
    default: 3,
  },
});

const emit = defineEmits(["loading-change"]);

const { t, tm } = useI18n();

const openedTaskIds = ref(new Set());
const fileInputRefs = ref(new Map());
const items = ref([]);
const progress = ref(0);
const inProgress = ref(false);
const sending = ref(false);
const iframe = ref({ open: false, url: "", type: "" });

const tasksQuery = useTaskTasksQuery(
  computed(() => Number(props.lessonId || 0)),
  computed(() => Number(props.pupilId || 0)),
  {
    retry: false,
  }
);

const createAnswerMutation = useCreateTaskAnswerMutation();
const updateAnswerMutation = useUpdateTaskAnswerMutation();
const uploadFileMutation = useUploadTaskFileMutation();

const isPending = computed(() => tasksQuery.isPending.value);
const statuses = computed(() => {
  const list = tm("status");
  return Array.isArray(list) ? list : [];
});

const isLoading = computed(
  () =>
    tasksQuery.isFetching.value ||
    sending.value ||
    inProgress.value ||
    createAnswerMutation.isPending.value ||
    updateAnswerMutation.isPending.value ||
    uploadFileMutation.isPending.value
);
const taskTabErrors = computed(() =>
  tasksQuery.isError.value ? [tasksQuery.error.value] : []
);

watch(
  () => isLoading.value,
  (value) => emit("loading-change", value),
  { immediate: true }
);

watch(
  () => tasksQuery.data.value,
  (value) => {
    const list = Array.isArray(value) ? value : [];
    items.value = list.map((item) => normalizeTask(item));
    openedTaskIds.value = new Set();
  },
  { immediate: true }
);

watch(
  () => props.pupilId,
  () => {
    items.value = [];
    openedTaskIds.value = new Set();
  }
);

function parseFiles(value) {
  if (Array.isArray(value)) return [...value];
  if (!value || typeof value !== "string") return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? [...parsed] : [];
  } catch {
    return [];
  }
}

function parseChat(value) {
  if (!value || typeof value !== "object") return [];
  if (Array.isArray(value.chatMessage)) return [...value.chatMessage];
  return [];
}

function normalizeTask(item) {
  const answerList = Array.isArray(item?.answer) ? item.answer : [];
  const last = answerList.length ? answerList[answerList.length - 1] : null;
  return {
    ...item,
    answerList,
    answer: {
      task_id: Number(last?.id || 0),
      lplans_tasks_id: Number(item?.task_id || 0),
      status: Number(last?.status || 0),
      updated_ts: last?.updated_ts || null,
      text: last?.answer || "",
      files: parseFiles(last?.files),
      chat: parseChat(last?.chat),
      childMessage: "",
    },
  };
}

function taskFiles(item) {
  if (Array.isArray(item?.info?.files) && item.info.files.length) return item.info.files;
  if (Array.isArray(item?.files)) return item.files;
  return [];
}

function getStatus(item) {
  const status = Number(item?.answer?.status || 0);
  if (status === 1)
    return { status: statuses.value[1] || "", class: "status--yellow", num: status };
  if (status === 2)
    return { status: statuses.value[2] || "", class: "status--green", num: status };
  if (status === 3)
    return { status: statuses.value[3] || "", class: "status--red", num: status };
  if (status === 4)
    return { status: statuses.value[4] || "", class: "status--yellow", num: status };
  if (status === 5)
    return { status: statuses.value[5] || "", class: "status--yellow", num: status };
  if (status === 6)
    return { status: statuses.value[6] || "", class: "status--yellow", num: status };
  return { status: statuses.value[0] || "", class: "", num: 0 };
}

function getBg(item) {
  const status = Number(item?.answer?.status || 0);
  if (status === 3) return { bg: "pink-bg", text: "danger" };
  if (status === 2) return { bg: "green-bg", text: "success" };
  if ([1, 4, 5, 6].includes(status)) return { bg: "yellow-bg", text: "warning" };
  return { bg: "", text: "" };
}

function getComment(item) {
  const chat = Array.isArray(item?.answer?.chat) ? item.answer.chat : [];
  if (!chat.length) return "";
  const currentPupilId = Number(props.pupilId || 0);
  const teacherMessages = chat.filter(
    (entry) => Number(entry?.person_id || 0) !== currentPupilId
  );
  if (!teacherMessages.length) return "";
  return String(teacherMessages[teacherMessages.length - 1]?.message || "");
}

function formatSendDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const locale = localStorage.getItem("translation") === "kz" ? "kk-KZ" : "ru-RU";
  return date.toLocaleString(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function taskEndDate(item) {
  if (!item?.end_date) return { status: false, text: "", date: null };
  const end = new Date(item.end_date);
  if (Number.isNaN(end.getTime())) return { status: false, text: "", date: null };
  const statusNum = getStatus(item).num;
  if (end.getTime() > Date.now()) {
    if ([0, 3, 6].includes(statusNum)) {
      const locale = localStorage.getItem("translation") === "kz" ? "kk-KZ" : "ru-RU";
      return {
        status: true,
        text: t("needToPass"),
        date: end.toLocaleString(locale, {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
      };
    }
    return { status: false, text: "", date: null };
  }
  return { status: true, text: t("timeEndPassTask"), date: null };
}

function isTaskOpen(id) {
  return openedTaskIds.value.has(id);
}

function toggleTask(id) {
  if (!id) return;
  if (openedTaskIds.value.has(id)) openedTaskIds.value.delete(id);
  else openedTaskIds.value.add(id);
  openedTaskIds.value = new Set(openedTaskIds.value);
}

function canEditAnswer(item) {
  return [0, 3, 6].includes(getStatus(item).num);
}

function checkTimeEnd(item) {
  if (!item?.end_date) return false;
  const end = new Date(item.end_date);
  if (Number.isNaN(end.getTime())) return false;
  return end.getTime() <= Date.now();
}

function parentRoleCheck(item) {
  if (Number(props.roleId || 3) === 3) return true;
  return getStatus(item).num !== 0;
}

function isAnswerDisabled(item) {
  return !canEditAnswer(item) || Number(props.roleId || 3) === 2 || checkTimeEnd(item);
}

function isFileUploadDisabled(item) {
  return isAnswerDisabled(item) || item.answer.files.length >= 10;
}

function checkMoreThen(count) {
  return Number(count || 0) >= 10;
}

function fileInputId(taskId) {
  return `task_file_${String(taskId || 0)}`;
}

function setFileInputRef(taskId, el) {
  if (!taskId) return;
  if (el) fileInputRefs.value.set(taskId, el);
  else fileInputRefs.value.delete(taskId);
}

function resetFileInput(taskId) {
  const el = fileInputRefs.value.get(taskId);
  if (el) el.value = "";
}

function removeFile(item, fileIndex) {
  item.answer.files.splice(fileIndex, 1);
}

async function uploadFiles(event, item) {
  const files = Array.from(event?.target?.files || []);
  for (let i = 0; i < files.length; i += 1) {
    inProgress.value = true;
    progress.value = 0;
    try {
      const uploaded = await uploadFileMutation.mutateAsync({
        file: files[i],
        onProgress: (value) => {
          progress.value = Number(value || 0);
        },
      });
      item.answer.files.push(uploaded);
    } finally {
      inProgress.value = false;
    }
  }
  resetFileInput(item.id);
}

function checkReadyToUpload(files, text) {
  const list = Array.isArray(files) ? files : [];
  if (!list.length) {
    return String(text || "").replace(/\s+/g, "") === "";
  }
  return false;
}

function isSubmitDisabled(item) {
  return (
    !canEditAnswer(item) ||
    checkReadyToUpload(item.answer.files, item.answer.text) ||
    inProgress.value ||
    checkTimeEnd(item)
  );
}

async function revertTask(item) {
  const answerId = Number(item?.answer?.task_id || 0);
  if (!answerId) return;
  sending.value = true;
  try {
    await updateAnswerMutation.mutateAsync({
      answerId,
      payload: { status: 6 },
    });
    await tasksQuery.refetch();
  } finally {
    sending.value = false;
  }
}

async function submit(item) {
  if (checkTimeEnd(item) || String(item.answer.text || "").length >= 2000) return;
  sending.value = true;
  try {
    if (!Number(item.answer.task_id || 0)) {
      await createAnswerMutation.mutateAsync({
        pupil_id: Number(props.pupilId || 0),
        lplans_tasks_id: Number(item.answer.lplans_tasks_id || 0),
        answer: item.answer.text || "",
        files: JSON.stringify(item.answer.files || []),
        message: item.answer.childMessage || "",
      });
    } else {
      await updateAnswerMutation.mutateAsync({
        answerId: Number(item.answer.task_id || 0),
        payload: {
          answer: item.answer.text || "",
          files: JSON.stringify(item.answer.files || []),
          status: 4,
          message: item.answer.childMessage || "",
        },
      });
    }
    await tasksQuery.refetch();
    resetFileInput(item.id);
  } finally {
    sending.value = false;
  }
}

function openFile(file) {
  const url = String(file?.url || "").trim();
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function getByFileType(file) {
  const mime = String(file?.type || "").toLowerCase();
  const ext = String(file?.ext || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  return ["doc", "docx", "ppt", "pptx", "xls", "xlsx", "pdf"].includes(ext);
}

function openFrame(file) {
  iframe.value = {
    open: true,
    url: String(file?.url || ""),
    type: String(file?.type || ""),
  };
}

function onTaskContentClick(event) {
  const anchor = event?.target?.closest?.("a");
  if (!anchor || !anchor.href) return;
  event.preventDefault();
  window.open(anchor.href, "_blank", "noopener,noreferrer");
}
</script>

<style scoped lang="scss">
.taskUploadFileName {
  color: #828282;
  word-break: break-word;
  font-size: 12px;
}
.task-list {
  margin: 0 8px;
}

.acc-item {
  width: 100%;
  border: none;
  background: inherit;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: 1px solid #e0e0e0;
}

.acc-item div p {
  color: #4f4f4f;
  opacity: 1;
}

.collapse-title {
  color: #4f4f4f;
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
}

.acc-icon {
  color: #828282;
  transition: transform 0.2s ease;
}

.acc-icon.is-open {
  transform: rotate(180deg);
}
.statusTime{
  font-size: 12px;
}
.status {
  color: #828282;
  font-size: 12px;
}

.status--red {
  color: #eb5757 !important;
}

.status--yellow {
  color: #f2a53a !important;
}

.status--green {
  color: #36b453 !important;
}

.red--color {
  color: #eb5757;
}

.task__header {
  padding: 12px;
  background: inherit;
}

.task__header__title {
  border-bottom: 1px dotted #bdbdbd;
}

.task__title {
  margin: 0;
  opacity: 0.8;
  font-size: 16px;
  line-height: 22px;
}

.task__sub-title {
  margin: 4px 0 0;
  opacity: 0.8;
}

.task__content {
  border-bottom: 1px dotted #bdbdbd;
  padding-bottom: 12px;
  color: #333;
}

.task__footer {
  background: #f5f5f5;
  padding: 12px 16px;
}

.answer__head {
  display: flex;
  justify-content: space-between;
}

.btn__revert {
  background: #36b453;
  border: none;
  color: #fff;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  padding: 2px 10px;
  font-size: 14px;
  height: 24px;
}

.textarea,
.input {
  width: 100%;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 8px;
  background: #fff;
}

.textarea:disabled,
.input:disabled {
  background: #f2f2f2;
  color: #8b8b8d;
  cursor: not-allowed;
}

.chat__msg {
  max-height: 120px;
  overflow: auto;
}

.chat__body {
  background: #fff;
  border-radius: 5px;
  padding: 10px 15px;
}

.child_file--btn {
  width: 100%;
  position: relative;
}

.child_file--btn [type="file"] {
  height: 0;
  width: 0;
  position: absolute;
  overflow: hidden;
}

.child_file--btn [type="file"] + label {
  border: 2px dashed #bdbdbd;
  border-radius: 4px;
  color: #828282;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  cursor: pointer;
  font-size: 12px;
  padding: 13px;
}

.link-item--green .green-icon {
  color: #36b453;
  cursor: pointer;
}

.submit {
  width: 100%;
  border: none;
  border-radius: 4px;
  background: #36b453;
  color: #fff;
  padding: 8px 12px;
  text-transform: uppercase;
  font-size: 13px;
}

.submit:disabled {
  opacity: 0.65;
  color: #fff !important;
  cursor: not-allowed;
}

.progress {
  height: 16px;
}

.progress-bar {
  background: #36b453;
  color: #fff;
  font-size: 11px;
  text-align: center;
}

.yellow-bg {
  background: #fff8e8 !important;
}

.green-bg {
  background: #f1fff4 !important;
}

.pink-bg {
  background: #fff5f7 !important;
}

.parentAlert {
  padding: 12px 20px;
  color: #828282;
}

.message-alert {
  padding: 12px;
}

</style>
